import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Download, Send, Loader2, Contact as ContactIcon } from "lucide-react";
import {
    BILLING,
    ACTING_RESUME_URL,
    DIRECTING_RESUME_URL,
    ALT_HEADSHOT,
} from "@/lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
    const [form, setForm] = useState({
        name: "",
        email: "",
        subject: "",
        message: "",
    });
    const [sending, setSending] = useState(false);

    const update = (k) => (e) =>
        setForm((prev) => ({ ...prev, [k]: e.target.value }));

    const downloadVCard = () => {
        const phoneClean = BILLING.phone.replace(/[^0-9+]/g, "");
        const vcard = [
            "BEGIN:VCARD",
            "VERSION:3.0",
            `N:Daignault;Vickie;;;`,
            `FN:${BILLING.name}`,
            `TITLE:${BILLING.tagline}`,
            `EMAIL;TYPE=INTERNET,PREF:${BILLING.email}`,
            `TEL;TYPE=CELL,VOICE:${phoneClean}`,
            `URL:${typeof window !== "undefined" ? window.location.origin : ""}`,
            `NOTE:${BILLING.union}`,
            "END:VCARD",
        ].join("\r\n");

        const blob = new Blob([vcard], { type: "text/vcard;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "Vickie-Daignault.vcf";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 1500);
        toast.success("Contact card saved.");
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.subject || !form.message) {
            toast.error("Please fill in every field.");
            return;
        }
        setSending(true);
        try {
            const res = await axios.post(`${API}/contact`, form);
            const status = res.data?.status;
            if (status === "sent") {
                toast.success("Message sent — Vickie will be in touch.");
            } else {
                toast.success(
                    res.data?.message ||
                        "Message received. Vickie will be in touch.",
                );
            }
            setForm({ name: "", email: "", subject: "", message: "" });
        } catch (err) {
            const detail =
                err?.response?.data?.detail || "Something went wrong. Try again.";
            toast.error(typeof detail === "string" ? detail : "Could not send");
        } finally {
            setSending(false);
        }
    };

    return (
        <section
            id="contact"
            data-testid="contact-section"
            className="bg-[#F7F5F0] py-24 md:py-32 border-t border-[#D1CFC9]"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="grid grid-cols-12 gap-6 md:gap-16">
                    {/* Left side */}
                    <div className="col-span-12 md:col-span-5">
                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.7 }}
                        >
                            <p className="eyebrow">§ 07</p>
                            <h2 className="font-serif-display text-5xl md:text-6xl lg:text-7xl mt-3 leading-[0.92] text-[#1A1A1A]">
                                Now,
                                <br />
                                about that{" "}
                                <span className="italic">working
                                <br />
                                with you</span>
                                ...
                            </h2>
                        </motion.div>

                        <div className="mt-12 grid gap-6">
                            <div>
                                <p className="eyebrow">Direct</p>
                                <a
                                    href={`mailto:${BILLING.email}`}
                                    data-testid="contact-email-link"
                                    className="font-serif-display text-2xl md:text-3xl text-[#1A1A1A] link-underline mt-1 inline-block"
                                >
                                    {BILLING.email}
                                </a>
                            </div>
                            <div>
                                <p className="eyebrow">Phone</p>
                                <a
                                    href={`tel:${BILLING.phone.replace(/\D/g, "")}`}
                                    data-testid="contact-phone-link"
                                    className="font-serif-display text-2xl md:text-3xl text-[#1A1A1A] link-underline mt-1 inline-block"
                                >
                                    {BILLING.phone}
                                </a>
                            </div>
                            <div>
                                <p className="eyebrow">Unions</p>
                                <p className="text-sm mt-1">{BILLING.union}</p>
                            </div>
                        </div>

                        <div className="mt-10">
                            <button
                                type="button"
                                onClick={downloadVCard}
                                data-testid="save-contact-vcard"
                                className="btn-ink"
                            >
                                <ContactIcon size={14} />
                                Save Contact Card
                            </button>
                        </div>

                        <div className="mt-12 pt-8 border-t border-[#D1CFC9]">
                            <p className="eyebrow mb-4">Press Kit</p>
                            <div className="flex flex-wrap gap-3">
                                <a
                                    href={ALT_HEADSHOT}
                                    download="Vickie-Daignault-Headshot.jpg"
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid="press-download-headshot"
                                    className="btn-outline"
                                >
                                    <Download size={14} />
                                    Headshot JPG
                                </a>
                                <a
                                    href={ACTING_RESUME_URL}
                                    download="Vickie-Daignault-Acting-Resume.pdf"
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid="press-download-acting"
                                    className="btn-outline"
                                >
                                    <Download size={14} />
                                    Acting PDF
                                </a>
                                <a
                                    href={DIRECTING_RESUME_URL}
                                    download="Vickie-Daignault-Directing-Resume.pdf"
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid="press-download-directing"
                                    className="btn-outline"
                                >
                                    <Download size={14} />
                                    Directing PDF
                                </a>
                                <button
                                    type="button"
                                    onClick={() =>
                                        toast.info(
                                            "EPK coming soon — send Vickie the file and it'll appear here.",
                                        )
                                    }
                                    data-testid="press-download-epk"
                                    className="btn-outline opacity-60 cursor-not-allowed"
                                    aria-disabled="true"
                                >
                                    <Download size={14} />
                                    EPK
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Right side — form */}
                    <div className="col-span-12 md:col-span-7">
                        <form
                            onSubmit={submit}
                            data-testid="contact-form"
                            className="grid gap-2 md:gap-4"
                        >
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-6">
                                <label className="block">
                                    <span className="eyebrow block mb-2">
                                        Name
                                    </span>
                                    <input
                                        type="text"
                                        value={form.name}
                                        onChange={update("name")}
                                        data-testid="contact-name-input"
                                        className="input-edit"
                                        placeholder="Your name"
                                        required
                                    />
                                </label>
                                <label className="block">
                                    <span className="eyebrow block mb-2">
                                        Email
                                    </span>
                                    <input
                                        type="email"
                                        value={form.email}
                                        onChange={update("email")}
                                        data-testid="contact-email-input"
                                        className="input-edit"
                                        placeholder="you@studio.com"
                                        required
                                    />
                                </label>
                            </div>
                            <label className="block">
                                <span className="eyebrow block mb-2">
                                    Subject
                                </span>
                                <input
                                    type="text"
                                    value={form.subject}
                                    onChange={update("subject")}
                                    data-testid="contact-subject-input"
                                    className="input-edit"
                                    placeholder="Project, audition, or inquiry"
                                    required
                                />
                            </label>
                            <label className="block">
                                <span className="eyebrow block mb-2">
                                    Message
                                </span>
                                <textarea
                                    rows="6"
                                    value={form.message}
                                    onChange={update("message")}
                                    data-testid="contact-message-input"
                                    className="input-edit resize-none"
                                    placeholder="Tell me about the project."
                                    required
                                />
                            </label>
                            <div className="flex justify-end mt-6">
                                <button
                                    type="submit"
                                    disabled={sending}
                                    data-testid="contact-submit-button"
                                    className="btn-ink disabled:opacity-50"
                                >
                                    {sending ? (
                                        <>
                                            <Loader2
                                                size={14}
                                                className="animate-spin"
                                            />
                                            Sending
                                        </>
                                    ) : (
                                        <>
                                            <Send size={14} />
                                            Send Message
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </section>
    );
}
